import React, { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MobileMenu from "../../MobileMenu/MobileMenu";
import styles from "./MobileHeader.module.css";
import { useSelector } from "react-redux";
import LocationModal from "../../LocationModal/LocationModal";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CloseIcon from "@mui/icons-material/Close";
import useSearch from "../../../hooks/useSearch";
import { useNavigate } from "react-router-dom";
import { getCategoryTree } from "../../../lib/getCategoryTree";
import LocationDisplay from "../components/LocationDisplay/LocationDisplay";
import { useOptimisticCartQuantity } from "../../../hooks/useOptimisticCartQuantity";

const MobileHeader = ({ toggleMiniCart }: { toggleMiniCart: () => void }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Use optimistic cart quantity for real-time sync across components
  const cartQuantity = useOptimisticCartQuantity();

  const { query, setQuery, results, handleSearch } = useSearch();
  const [trendingTerms] = useState([
    "Halloween decorations",
    "Christmas trees",
    "Winter jackets",
    "Smartphones",
    "Laptops",
  ]);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const location = useSelector((state: any) => state.location);

  useEffect(() => {
    if (isSearchActive) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isSearchActive]);

  const handleSearchClick = () => setIsSearchActive(true);
  const handleCancelSearch = () => {
    setIsSearchActive(false);
    setQuery("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value) handleSearch(value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      handleCancelSearch(); // Close search overlay after navigation
    }
  };

  const handleTrendingItemClick = (term: string) => {
    navigate(`/search?q=${encodeURIComponent(term)}`);
    handleCancelSearch(); // Close search overlay after navigation
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const [categoryTree, setCategoryTree] = useState<any[]>([]);

  useEffect(() => {
    const fetchCategoryTree = async () => {
      const tree = await getCategoryTree();
      setCategoryTree(tree);
    };

    fetchCategoryTree();
  }, []);

  console.log("tree", categoryTree);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerContainer}>
          {!isSearchActive && (
            <>
              <div
                className={styles.menuIcon}
                onClick={toggleMenu}
                role="button"
                tabIndex={0}
                aria-label="Abrir menú principal"
                title="Menú principal"
                onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
              >
                <MenuIcon style={{ width: "24px" }} aria-hidden="true" />
              </div>
              <div className={styles.logoContainer}>
                <a
                  href="/"
                  title="Ir a la página principal de Evefant"
                  aria-label="Página principal de Evefant"
                >
                  <img
                    src={`${process.env.REACT_APP_IMAGE_FOLDER}/isotipo_Evefant_azul_b2dbc10d2e.webp`}
                    alt="Logo de Evefant"
                    className={styles.logo}
                    title="Evefant - Tu tienda online"
                    width={36}
                    height={36}
                  />
                </a>
              </div>
            </>
          )}

          <div
            className={`${styles.searchContainer} ${
              isSearchActive ? styles.expandedSearch : ""
            }`}
          >
            <form
              onSubmit={handleSearchSubmit}
              className={styles.searchWrapper}
              role="search"
              aria-label="Buscador de productos"
            >
              <SearchOutlinedIcon
                className={styles.searchIcon}
                aria-hidden="true"
              />
              <input
                type="text"
                placeholder="Buscar..."
                value={query}
                onChange={handleInputChange}
                onClick={handleSearchClick}
                className={styles.searchInput}
                aria-label="Buscar productos en Evefant"
                title="Buscar productos"
              />
              {isSearchActive && (
                <button
                  type="button"
                  className={styles.closeIconButton}
                  onClick={handleCancelSearch}
                  aria-label="Cancelar búsqueda"
                  title="Cancelar búsqueda"
                >
                  <CloseIcon className={styles.closeIcon} aria-hidden="true" />
                </button>
              )}
            </form>
          </div>

          {!isSearchActive && (
            <div
              className={styles.cartIcon}
              onClick={toggleMiniCart}
              role="button"
              tabIndex={0}
              aria-label={`Carrito de compras, ${cartQuantity} ${
                cartQuantity === 1 ? "artículo" : "artículos"
              }`}
              title="Ver carrito de compras"
              onKeyDown={(e) => e.key === "Enter" && toggleMiniCart()}
            >
              <ShoppingCartOutlinedIcon aria-hidden="true" />
              {cartQuantity > 0 && (
                <div className={styles.cartBadge} aria-hidden="true">
                  {cartQuantity}
                </div>
              )}
            </div>
          )}
        </div>

        {!isSearchActive && (
          <div
            className={styles.locationContainer}
            onClick={() => setLocationModalOpen(true)}
            role="button"
            tabIndex={0}
            aria-label="Seleccionar ubicación de entrega"
            title="Cambiar ubicación de entrega"
            onKeyDown={(e) => e.key === "Enter" && setLocationModalOpen(true)}
          >
            <LocationDisplay location={location} variant="mobile" />
            <ArrowDropDownIcon
              className={styles.arrowIcon}
              aria-hidden="true"
            />
            {locationModalOpen && (
              <LocationModal
                isOpen={locationModalOpen}
                onClose={() => setLocationModalOpen(false)}
              />
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: "120px" }} />

      {isSearchActive && (
        <div
          className={styles.searchResults}
          role="region"
          aria-label="Resultados de búsqueda"
        >
          {!query && (
            <div className={styles.trendingSection}>
              <h3>Populares</h3>
              <ul className={styles.trendingList}>
                {trendingTerms.map((term, index) => (
                  <li
                    key={index}
                    className={styles.trendingItem}
                    onClick={() => handleTrendingItemClick(term)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Buscar ${term}`}
                    title={`Buscar "${term}"`}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleTrendingItemClick(term)
                    }
                  >
                    {term}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {query && results.length > 0 && (
            <div className={styles.resultsContainer}>
              {results.map((result: any) => (
                <div
                  key={result.id}
                  className={styles.resultItem}
                  onClick={() => {
                    navigate(`/product/${result.url}`);
                    handleCancelSearch();
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Ver producto: ${result.name}`}
                  title={`Ver detalles de ${result.name}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate(`/product/${result.url}`);
                      handleCancelSearch();
                    }
                  }}
                >
                  {result.name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isMenuOpen && (
        <MobileMenu categories={categoryTree} toggleMenu={toggleMenu} />
      )}
    </>
  );
};

export default MobileHeader;
